import { yupResolver } from '@hookform/resolvers/yup'
import { Block } from 'baseui/block'
import { Button, KIND } from 'baseui/button'
import { FormControl } from 'baseui/form-control'
import { Input } from 'baseui/input'
import { Tab, Tabs } from 'baseui/tabs-motion'
import { TimezonePicker } from 'baseui/timezonepicker'
import { HeadingXSmall } from 'baseui/typography'
import { Location } from 'client'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { createCallbackRef } from 'use-callback-ref'
import * as yup from 'yup'

import { useApi } from '../../../ApiProvider'
import { useLoadIndicator } from '../../load-indicator'
import { Machines } from '../machines/machines'

export const LocationDetails: React.FC<{ activeTab: 'details' | 'machines' }> =
  React.memo(({ activeTab }) => {
    const [location, setLocation] = useState<Location>()
    const [t] = useTranslation()
    const params = useParams()
    const { locationApi } = useApi()
    const { startLoading, stopLoading } = useLoadIndicator()
    const navigate = useNavigate()

    const locationSchema: yup.ObjectSchema<Location> = useMemo(() => {
      return yup.object({
        id: yup.number(),
        name: yup
          .string()
          .required(t('validation.required'))
          .label(t('settings.location.name')),
        latitude: yup.number(),
        longitude: yup.number(),
        timezone: yup
          .string()
          .required(t('validation.required'))
          .label(t('settings.location.timezone')),
        tenantId: yup.string(),
      })
    }, [t])

    const {
      control,
      handleSubmit,
      formState: { errors, isDirty, isValid },
    } = useForm<Location>({
      resolver: yupResolver(locationSchema),
      mode: 'onChange',
      reValidateMode: 'onChange',
      defaultValues: {
        name: '',
        timezone: 'Europe/Berlin',
      },
      values: location,
    })

    useEffect(() => {
      if (params.locationId) {
        startLoading()
        locationApi
          .getLocation(parseInt(params.locationId))
          .then((response) => {
            setLocation(response.data)
          })
          .finally(stopLoading)
      }
    }, [locationApi, params, startLoading, stopLoading])

    if (!location) {
      return null
    }

    return (
      <>
        <Block
          paddingTop={'scale600'}
          paddingRight={'scale600'}
          paddingLeft={'scale600'}
        >
          <HeadingXSmall marginTop={'scale300'} marginBottom={'scale600'}>
            {location.name}
          </HeadingXSmall>
        </Block>
        <Block flex={'auto'} display={'flex'} flexDirection={'column'}>
          <Tabs
            activeKey={activeTab}
            onChange={({ activeKey }) => {
              if (activeKey !== activeTab) {
                navigate({ pathname: `../${activeKey}` }, { relative: 'path' })
              }
            }}
            activateOnFocus
            overrides={{
              Root: {
                style: {
                  flex: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                },
              },
            }}
          >
            <Tab key={'details'} title={t('settings.tenant.details')}>
              <form
                onSubmit={handleSubmit((location: Location) => {
                  locationApi
                    .updateLocation(location.id!, location)
                    .then((response) => setLocation(response.data))
                })}
              >
                <FormControl
                  label={t('settings.tenant.name')}
                  error={errors?.name?.message}
                >
                  <Controller
                    name={'name'}
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <Input
                        onChange={onChange}
                        onBlur={onBlur}
                        placeholder={t('settings.tenant.name')}
                        value={value}
                        error={!!errors?.name}
                        inputRef={createCallbackRef(ref)}
                      />
                    )}
                  />
                </FormControl>
                <FormControl
                  label={t('settings.location.timezone')}
                  error={errors?.timezone?.message}
                >
                  <Controller
                    name={'timezone'}
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <TimezonePicker
                        onChange={(value) => {
                          if (value) {
                            onChange(value?.id)
                          }
                        }}
                        value={value}
                        error={!!errors?.timezone}
                        overrides={{
                          Select: {
                            props: {
                              onBlur,
                              controlRef: createCallbackRef(ref),
                            },
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
                <Button
                  disabled={!isValid || !isDirty}
                  type={'submit'}
                  overrides={{
                    Root: {
                      style: ({ $theme }) => ({
                        marginRight: $theme.sizing.scale600,
                      }),
                    },
                  }}
                >
                  Save
                </Button>
                <Button
                  onClick={() => navigate({ pathname: '../../locations' })}
                  kind={KIND.tertiary}
                  type={'reset'}
                >
                  Cancel
                </Button>
              </form>
            </Tab>
            <Tab
              key={'machines'}
              title={t('settings.location.machines')}
              overrides={{
                TabPanel: {
                  style: {
                    paddingLeft: 0,
                    paddingRight: 0,
                    paddingBottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  },
                },
              }}
            >
              <Machines />
            </Tab>
          </Tabs>
        </Block>
      </>
    )
  })
